import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ShortInfoModalComponent } from '../short-info-modal/short-info-modal.component';
import ArticleGroup from '../../../smoothr-web-app-core/models/ArticleGroup';
import { getAvailability, getPrice, numberToCurrency } from '../../../smoothr-web-app-core/utils/utils';
import RepositoryDirective from '../../../smoothr-web-app-core/directives/repository-directive';
import { filterMatchingOptions, OrderUtils } from '../../../smoothr-web-app-core/utils/order-utils';
import { ValidationUtils } from '../../../smoothr-web-app-core/utils/validation-utils';
import { DisplayMode } from '../../../smoothr-web-app-core/enums/DisplayMode';
import OptionGroup from '../../../smoothr-web-app-core/models/OptionGroup';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import Article from '../../../smoothr-web-app-core/models/Article';
import ArticleOption from '../../../smoothr-web-app-core/models/ArticleOption';
import FulfilledDependency from '../../../smoothr-web-app-core/models/FulfilledDependency';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import { environment } from 'src/environments/environment';
import { OrderType } from 'src/smoothr-web-app-core/enums/OrderType';
import { AllergensInfoModalComponent } from '../allergens-info-modal/allergens-info-modal.component';
import { RecomendationArticleModalComponent } from '../recomendation-article-modal/recomendation-article-modal.component';
import { isDoubleMonday } from '../../shared';
import { DoubleMondayModal, DoubleMondayModalResult } from '../double-monday-modal/double-monday-modal';

@Component({
	selector: 'app-modal-info',
	templateUrl: './modal-info.component.html',
	styleUrls: ['modal-info.component.scss'],
	standalone: false,
})
export class ModalInfoComponent extends RepositoryDirective implements OnInit, AfterViewInit {
	@ViewChild('contentArea', { static: false }) contentArea: ElementRef;
	environment = environment;
	articleGroup: ArticleGroup;
	maxQuantity: number;

	OrderUtils = OrderUtils;
	validationUtils = ValidationUtils;
	findFeatureArray: string[] = [];
	dm = DisplayMode;
	errorOptionItemId: string = null;
	numberToCurrency = numberToCurrency;
	getPrice = getPrice;
	filterMatchingOptions = filterMatchingOptions;
	checkIfButtonFinishedAvailable = false;
	showMostWanted: boolean = false;
	isCloseButtonValid: boolean = false;
	checkVeganFeatureValue = '';
	public filtersValues: string[] = [];

	constructor(
		public modalCtrl: ModalController,
		protected repository: RepositoryService,
		private snackBarCtrl: MatSnackBar,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef
	) {
		super(repository);
	}

	static async show(
		modalCtrl: ModalController,
		articleGroup: ArticleGroup,
		recommend: boolean = true,
		cssClass: string = 'item-modal',
		mode: 'ios' | 'md' = undefined,
		noQuantity: boolean = false,
		maxQuantity: number = 99
	): Promise<ArticleGroup | null> {
		const modal = await modalCtrl.create({
			cssClass,
			component: ModalInfoComponent,
			componentProps: {
				articleGroup,
				recommend,
				noQuantity,
				maxQuantity,
			},
			mode,
			showBackdrop: true,
			backdropDismiss: true,
		});
		await modal.present();
		const result = (await modal.onDidDismiss()).data;
		if (!result) {
			return null;
		}
		return result as ArticleGroup;
	}

	get type(): PreorderType {
		return this.order.preorder.type;
	}

	ngOnInit() {
		super.ngOnInit();

		if (this.articleGroup.article.tags.find(it => it.identifier === 'most_wanted')) {
			this.showMostWanted = true;
		}

		try {
			this.isCloseButtonValid = ValidationUtils.areGroupsValid(this.articleGroup, this.articleGroup.article.groups);
		} catch (e) {
			this.isCloseButtonValid = false;
		}
		this.checkVeganFeature(this.articleGroup.article);
		this.findFeature(this.articleGroup.article);
	}

	async ngAfterViewInit() {
		this.checkIfHaveGroups();
	}

	async handleDoubleMonday(modalCtrl: ModalController, repositoryService: RepositoryService) {
		const isDoubleMondayArticle = isDoubleMonday(repositoryService, this.articleGroup.article);
		if (!isDoubleMondayArticle) {
			return;
		}
		const doubleMondayResult = await DoubleMondayModal.show(modalCtrl);

		if (doubleMondayResult !== DoubleMondayModalResult.CONFIRM) {
			return;
		}

		const doubleMondayAdded = repositoryService._order.promoCodes.some(it => it.code === environment.doubleMondayCode);
		if (!doubleMondayAdded) {
			await repositoryService.addDoubleMonday();
		}
		this.articleGroup.isDoubleMonday = true;
		this.articleGroup.quantity *= 2;
	}

	async finish() {
		await this.handleDoubleMonday(this.modalCtrl, this.repository);
		await this.modalCtrl.dismiss(this.articleGroup);
	}

	async close() {
		await this.modalCtrl.dismiss();
	}

	increase() {
		if (this.articleGroup.quantity >= this.maxQuantity) {
			return;
		}
		this.articleGroup.quantity += 1;
	}

	decrease() {
		if (this.articleGroup.quantity > 1) {
			this.articleGroup.quantity -= 1;
		}
	}

	async add(
		pageIndex: number,
		optionGroupIndex: number,
		option: ArticleOption,
		group: OptionGroup,
		fulfilledDependency: FulfilledDependency,
		dependencyNumber: number
	) {
		this.errorOptionItemId = null;
		OrderUtils.addOptionNew(this.articleGroup.groups, option, group, fulfilledDependency, dependencyNumber);
		this.isCloseButtonValid = ValidationUtils.areGroupsValid(this.articleGroup, this.articleGroup.article.groups);
		this.articleGroup.groups = this.articleGroup.groups.filter(group => {
			const foundOption = this.articleGroup.article.groups.find(it => it._id === group.group);
			if (!foundOption) {
				return false;
			}
			const dependencies = ValidationUtils.isGroupDependencyFulfilled(
				this.articleGroup.article,
				this.articleGroup.groups,
				foundOption
			);
			return !((dependencies.times > 0 && group.dependencyNumber === 0) || group.dependencyNumber > dependencies.times);
		});
		this.articleGroup.article.groups.forEach(grp => {
			if (ValidationUtils.areGroupsValid(this.articleGroup, [grp])) {
				return;
			}
			if (grp.limit != grp.requiredAmount || grp.limit != grp.articles.length || grp.hasMultiple) {
				return;
			}

			const dependency = ValidationUtils.isGroupDependencyFulfilled(this.articleGroup.article, this.articleGroup.groups, grp);
			console.log(dependency);
			for (let depNumber = 1; depNumber <= dependency.times; depNumber++) {
				grp.articles.forEach(opt => {
					const option = new ArticleOption();
					option.article = opt;
					option.group = grp._id;
					option.dependency = dependency?.dependency?._id;
					option.dependencyNumber = depNumber;
					option.dependsOn = dependency?.dependsOn;
					option.quantity = 1;
					OrderUtils.addOptionNew(this.articleGroup.groups, option, grp, dependency, depNumber);
				});
			}
		});
		console.log(
			this.articleGroup.article.name.de,
			this.articleGroup.groups.map(option => ({
				name: option.article.name,
				dependencyNumber: option.dependencyNumber,
				quantity: option.quantity,
			}))
		);
		this.isCloseButtonValid = ValidationUtils.areGroupsValid(this.articleGroup, this.articleGroup.article.groups);

		if (ValidationUtils.areGroupsValid(this.articleGroup, [group])) {
			const nextGroup = this.findNextIncompleteGroup(optionGroupIndex);
			if (nextGroup) {
				this.scrollToElement(nextGroup._id);
			}
		}

		// Trigger change detection
		this.cdr.detectChanges();
	}

	findNextIncompleteGroup(currentIndex: number): OptionGroup | null {
		const groups = this.articleGroup.article.groups;
		for (let i = currentIndex + 1; i < groups.length; i++) {
			const group = groups[i];
			if (group.visible && !ValidationUtils.areGroupsValid(this.articleGroup, [group])) {
				return group;
			}
		}
		return null;
	}

	scrollToElement(elementId: string) {
		const element = document.getElementById(elementId);
		if (element) {
			const container = this.contentArea.nativeElement;
			const offset = 150; // 100px offset
			const elementPosition = element.getBoundingClientRect().top;
			const offsetPosition = elementPosition + container.scrollTop - offset;

			container.scrollTo({
				top: offsetPosition,
				behavior: 'smooth',
			});
		}
	}

	async addToOrder(finished: boolean = false) {
		this.errorOptionItemId = null;

		if (!ValidationUtils.areGroupsValid(this.articleGroup, this.articleGroup.article.groups)) {
			const result = ValidationUtils.findInvalidGroup(this.articleGroup, this.articleGroup.article.groups);
			if (result) {
				console.log('Invalid group found:', result);
				console.log(
					'Options for this group:',
					this.articleGroup.groups.filter(opt => opt.group === result._id)
				);
				this.errorOptionItemId = result._id;
				this.scrollToElement(result._id);
			} else {
				console.log('No specific invalid group found, showing generic error');
				this.snackBarCtrl.open(this.translate.instant('modal_info.pick_error'), null, {
					duration: 2000,
				});
			}
			return;
		}
		console.log('Validation passed, adding to order');

		if (finished) {
			// Close modal after adding to cart
			await this.finish();
		}
	}

	async openInfo(article: Article) {
		await ShortInfoModalComponent.show(this.modalCtrl, article.info);
	}

	checkIfHaveGroups() {
		this.checkIfButtonFinishedAvailable =
			this.articleGroup.article.groups.length > 0 &&
			!this.articleGroup.article.tags.find(it => it?.identifier?.includes('deposit')) &&
			this.articleGroup.article.groups.filter(it => it.requiredAmount > 0 && it.visible)?.length === 0;
	}

	isDeinstag(): boolean {
		return !!this.articleGroup.article.tags.find(it => it.identifier === 'deinstag');
	}

	showNutrition(article: Article) {
		AllergensInfoModalComponent.show(this.modalCtrl, article);
	}

	generateDependencies(fulfilledDependency: FulfilledDependency): number[] {
		if (fulfilledDependency.times === 0) return [0];
		if (fulfilledDependency.times === -1) return [];
		const dependencyNumbers: number[] = [];
		for (let dependencyNumber = 1; dependencyNumber <= fulfilledDependency.times; dependencyNumber++) {
			dependencyNumbers.push(dependencyNumber);
		}
		return dependencyNumbers;
	}

	checkVeganFeature(article: Article) {
		const result = Object.entries(article.compound?.features)
			.filter(it => it[0] == 'vegetarian' || it[0] == 'vegan')
			.filter(it => !!it[1])
			.map(it => it[0]);
		if (result && result?.length > 1) {
			this.checkVeganFeatureValue = result[1];
		} else {
			this.checkVeganFeatureValue = result[0];
		}
	}

	checkIfArticleIsVisible(optionGroup: OptionGroup): boolean {
		const visibleArticles = optionGroup?.articles?.filter(
			it => it?.visible && it?.isActive && getAvailability(it, OrderType.STANDARD, null)
		);
		return visibleArticles?.length > 0;
	}

	findFeature(article: Article) {
		if (this.findFeatureArray.length > 0) {
			return;
		}
		if (article) {
			if (article?.compound?.allergens && article.compound.allergens['cerealsContainingGluten'] == true) {
				this.findFeatureArray.push('gluten');
			}
			if (article?.compound?.allergens && article.compound.allergens['peanutsAndProductsThereof'] == true) {
				this.findFeatureArray.push('peanut');
			}
			if (article?.compound?.allergens && article.compound.allergens['milkAndProductsThereof'] == true) {
				this.findFeatureArray.push('lactose');
			}
			console.log(article.name.de, this.findFeatureArray);
		}
	}
	goBack() {
		this.close();
	}
}
